/**
 * Fallback Layout Engine
 *
 * Generates kitchen layout suggestions using rule-based logic.
 * Used when AI is unavailable or as a baseline comparison.
 *
 * Layout Rules:
 * - Sink: Near window (if exists), else on longest wall
 * - Fridge: Near door/entrance for grocery unloading
 * - Cooktop: On opposite wall from sink, with ventilation access
 * - Work triangle should be 4-8m perimeter
 */

import type {
  RoomConfig,
  WallSegment,
  StructuralElement,
  PlacedVolume,
  Point3D,
  LayoutPlacement,
  LayoutSuggestion,
  LayoutSuggestResponse,
  ProductResponse,
} from "@byk/shared";
import {
  calculateTriangleMetrics,
  calculateTriangleScore,
} from "./workTriangle";

// =============================================================================
// Configuration
// =============================================================================

/** Default product IDs for work triangle items */
const DEFAULT_PRODUCTS = {
  sink: "p1", // Niga Dual Basin Kosher
  cooktop: "p3", // Teka Induction Cooktop
  fridge: "p9", // Teka Built-in Refrigerator
};

/** Standard counter depth (cm) */
const COUNTER_DEPTH = 60;

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get wall by ID
 */
function getWallById(walls: WallSegment[], id: string): WallSegment | undefined {
  return walls.find((w) => w.id === id);
}

/**
 * Calculate wall length
 */
function getWallLength(wall: WallSegment): number {
  const dx = wall.end.x - wall.start.x;
  const dy = wall.end.y - wall.start.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Get position along a wall at a given offset from start
 */
function getPositionOnWall(
  wall: WallSegment,
  offset: number,
  inset: number = COUNTER_DEPTH
): Point3D {
  const length = getWallLength(wall);
  const t = Math.min(offset / length, 1);

  // Direction along wall
  const dx = wall.end.x - wall.start.x;
  const dy = wall.end.y - wall.start.y;

  // Perpendicular inward (assumes counter-clockwise wall ordering)
  const perpX = -dy / length;
  const perpY = dx / length;

  return {
    x: wall.start.x + dx * t + perpX * inset,
    y: wall.start.y + dy * t + perpY * inset,
    z: 0,
  };
}

/**
 * Get rotation for an item placed against a wall
 */
function getWallRotation(wall: WallSegment): number {
  const dx = wall.end.x - wall.start.x;
  const dy = wall.end.y - wall.start.y;
  return (Math.atan2(dy, dx) * 180) / Math.PI;
}

/**
 * Find wall with a window
 */
function findWallWithWindow(
  walls: WallSegment[],
  elements: StructuralElement[]
): WallSegment | null {
  const windowElement = elements.find((e) => e.type === "window");
  if (!windowElement) return null;
  return getWallById(walls, windowElement.wallId) || null;
}

/**
 * Find wall with a door (entrance)
 */
function findWallWithDoor(
  walls: WallSegment[],
  elements: StructuralElement[]
): WallSegment | null {
  const doorElement = elements.find(
    (e) => e.type === "door" || e.type === "door_double"
  );
  if (!doorElement) return null;
  return getWallById(walls, doorElement.wallId) || null;
}

/**
 * Get opposite wall (for rectangular rooms)
 */
function getOppositeWall(
  wall: WallSegment,
  walls: WallSegment[]
): WallSegment | null {
  // Simple heuristic: find wall with similar length on opposite side
  const wallLength = getWallLength(wall);

  // Calculate wall center
  const wallCenterX = (wall.start.x + wall.end.x) / 2;
  const wallCenterY = (wall.start.y + wall.end.y) / 2;

  let bestWall: WallSegment | null = null;
  let maxDistance = 0;

  for (const other of walls) {
    if (other.id === wall.id) continue;

    const otherLength = getWallLength(other);
    // Similar length (within 20%)
    if (Math.abs(otherLength - wallLength) / wallLength > 0.2) continue;

    const otherCenterX = (other.start.x + other.end.x) / 2;
    const otherCenterY = (other.start.y + other.end.y) / 2;

    const distance = Math.sqrt(
      Math.pow(otherCenterX - wallCenterX, 2) +
        Math.pow(otherCenterY - wallCenterY, 2)
    );

    if (distance > maxDistance) {
      maxDistance = distance;
      bestWall = other;
    }
  }

  return bestWall;
}

/**
 * Get adjacent wall (perpendicular)
 */
function getAdjacentWall(
  wall: WallSegment,
  walls: WallSegment[],
  preferEnd: boolean = true
): WallSegment | null {
  const targetPoint = preferEnd ? wall.end : wall.start;

  for (const other of walls) {
    if (other.id === wall.id) continue;

    // Check if walls share a point
    if (
      (Math.abs(other.start.x - targetPoint.x) < 1 &&
        Math.abs(other.start.y - targetPoint.y) < 1) ||
      (Math.abs(other.end.x - targetPoint.x) < 1 &&
        Math.abs(other.end.y - targetPoint.y) < 1)
    ) {
      return other;
    }
  }

  return null;
}

/**
 * Get longest wall
 */
function getLongestWall(walls: WallSegment[]): WallSegment {
  let longest = walls[0];
  let maxLength = 0;

  for (const wall of walls) {
    const length = getWallLength(wall);
    if (length > maxLength) {
      maxLength = length;
      longest = wall;
    }
  }

  return longest;
}

// =============================================================================
// Layout Generators
// =============================================================================

/**
 * Generate L-shape layout
 * Sink and fridge on one wall, cooktop on adjacent wall
 */
function generateLShapeLayout(
  _room: RoomConfig,
  walls: WallSegment[],
  elements: StructuralElement[]
): LayoutSuggestion {
  const windowWall = findWallWithWindow(walls, elements);
  const doorWall = findWallWithDoor(walls, elements);

  // Primary wall: window wall or longest wall
  const primaryWall = windowWall || getLongestWall(walls);
  const primaryLength = getWallLength(primaryWall);

  // Secondary wall: adjacent to primary
  const secondaryWall = getAdjacentWall(primaryWall, walls) || walls[1];
  const secondaryLength = getWallLength(secondaryWall);

  // Place sink near window or center of primary wall
  const windowEl = elements.find(
    (e) => e.type === "window" && e.wallId === primaryWall.id
  );
  const sinkOffset = windowEl ? windowEl.offset : primaryLength / 2;

  // Place fridge at end of primary wall (near door if possible)
  const fridgeOffset = doorWall?.id === primaryWall.id ? 100 : primaryLength - 100;

  // Place cooktop on secondary wall
  const cooktopOffset = secondaryLength / 2;

  const placements: LayoutPlacement[] = [
    {
      role: "sink",
      productId: DEFAULT_PRODUCTS.sink,
      position: getPositionOnWall(primaryWall, sinkOffset),
      rotation: getWallRotation(primaryWall),
    },
    {
      role: "fridge",
      productId: DEFAULT_PRODUCTS.fridge,
      position: getPositionOnWall(primaryWall, fridgeOffset),
      rotation: getWallRotation(primaryWall),
    },
    {
      role: "cooktop",
      productId: DEFAULT_PRODUCTS.cooktop,
      position: getPositionOnWall(secondaryWall, cooktopOffset),
      rotation: getWallRotation(secondaryWall),
    },
  ];

  const triangle = calculateTriangleMetrics(placements);
  const score = calculateTriangleScore(triangle);

  return {
    id: `layout-l-${Date.now()}`,
    type: "L-shape",
    score,
    placements,
    triangle,
    reasoning: `L-shape layout with sink ${windowWall ? "under window" : "on main wall"}, fridge nearby, and cooktop on adjacent wall for optimal workflow.`,
    source: "fallback",
  };
}

/**
 * Generate U-shape layout
 * Work triangle items on three walls
 */
function generateUShapeLayout(
  room: RoomConfig,
  walls: WallSegment[],
  elements: StructuralElement[]
): LayoutSuggestion {
  if (walls.length < 3) {
    // Not enough walls for U-shape, fall back to L-shape positioning
    return {
      ...generateLShapeLayout(room, walls, elements),
      type: "U-shape",
      id: `layout-u-${Date.now()}`,
    };
  }

  const windowWall = findWallWithWindow(walls, elements);
  const primaryWall = windowWall || walls[0];

  // Get two adjacent walls
  const leftWall = getAdjacentWall(primaryWall, walls, false) || walls[1];
  const rightWall = getAdjacentWall(primaryWall, walls, true) || walls[2];

  const primaryLength = getWallLength(primaryWall);
  const leftLength = getWallLength(leftWall);
  const rightLength = getWallLength(rightWall);

  const placements: LayoutPlacement[] = [
    {
      role: "sink",
      productId: DEFAULT_PRODUCTS.sink,
      position: getPositionOnWall(primaryWall, primaryLength / 2),
      rotation: getWallRotation(primaryWall),
    },
    {
      role: "fridge",
      productId: DEFAULT_PRODUCTS.fridge,
      position: getPositionOnWall(leftWall, leftLength / 2),
      rotation: getWallRotation(leftWall),
    },
    {
      role: "cooktop",
      productId: DEFAULT_PRODUCTS.cooktop,
      position: getPositionOnWall(rightWall, rightLength / 2),
      rotation: getWallRotation(rightWall),
    },
  ];

  const triangle = calculateTriangleMetrics(placements);
  const score = calculateTriangleScore(triangle);

  return {
    id: `layout-u-${Date.now()}`,
    type: "U-shape",
    score,
    placements,
    triangle,
    reasoning:
      "U-shape layout distributes work triangle across three walls, maximizing counter space and creating efficient workflow zones.",
    source: "fallback",
  };
}

/**
 * Generate Galley layout
 * Work triangle items on two parallel walls
 */
function generateGalleyLayout(
  _room: RoomConfig,
  walls: WallSegment[],
  elements: StructuralElement[]
): LayoutSuggestion {
  const windowWall = findWallWithWindow(walls, elements);
  const primaryWall = windowWall || getLongestWall(walls);
  const oppositeWall = getOppositeWall(primaryWall, walls) || walls[1];

  const primaryLength = getWallLength(primaryWall);
  const oppositeLength = getWallLength(oppositeWall);

  // Sink near window on primary wall
  const windowEl = elements.find(
    (e) => e.type === "window" && e.wallId === primaryWall.id
  );
  const sinkOffset = windowEl ? windowEl.offset : primaryLength / 3;

  // Cooktop on primary wall, away from sink
  const cooktopOffset = primaryLength * 0.7;

  // Fridge on opposite wall
  const fridgeOffset = oppositeLength / 2;

  const placements: LayoutPlacement[] = [
    {
      role: "sink",
      productId: DEFAULT_PRODUCTS.sink,
      position: getPositionOnWall(primaryWall, sinkOffset),
      rotation: getWallRotation(primaryWall),
    },
    {
      role: "cooktop",
      productId: DEFAULT_PRODUCTS.cooktop,
      position: getPositionOnWall(primaryWall, cooktopOffset),
      rotation: getWallRotation(primaryWall),
    },
    {
      role: "fridge",
      productId: DEFAULT_PRODUCTS.fridge,
      position: getPositionOnWall(oppositeWall, fridgeOffset),
      rotation: getWallRotation(oppositeWall) + 180, // Face opposite direction
    },
  ];

  const triangle = calculateTriangleMetrics(placements);
  const score = calculateTriangleScore(triangle);

  return {
    id: `layout-galley-${Date.now()}`,
    type: "Galley",
    score,
    placements,
    triangle,
    reasoning:
      "Galley layout places sink and cooktop on one wall with fridge opposite, ideal for narrow kitchens with good traffic flow.",
    source: "fallback",
  };
}

// =============================================================================
// Main Engine Function
// =============================================================================

/**
 * Generate layout suggestions using fallback rule-based engine
 */
export function generateFallbackLayouts(
  room: RoomConfig,
  walls: WallSegment[],
  elements: StructuralElement[],
  _existingVolumes?: PlacedVolume[],
  _products?: ProductResponse[]
): LayoutSuggestResponse {
  // Check if we have enough walls to generate layouts
  if (!walls || walls.length < 2) {
    console.warn("[LayoutEngine] Not enough walls defined. Need at least 2 walls for layout generation.");
    // Return a default single layout based on room dimensions
    const defaultLayout = generateDefaultLayout(room);
    return {
      layouts: [defaultLayout],
      usedFallback: true,
    };
  }

  // Generate three layout options
  const layouts: LayoutSuggestion[] = [
    generateLShapeLayout(room, walls, elements),
    generateUShapeLayout(room, walls, elements),
    generateGalleyLayout(room, walls, elements),
  ];

  // Sort by score descending
  layouts.sort((a, b) => b.score - a.score);

  return {
    layouts,
    usedFallback: true,
  };
}

/**
 * Generate a default layout when walls are not defined
 * Uses room dimensions to create a simple L-shape layout
 */
function generateDefaultLayout(room: RoomConfig): LayoutSuggestion {
  const margin = COUNTER_DEPTH + 10; // Counter depth + clearance

  // Place sink on the back wall (y = depth - margin)
  const sinkPos: Point3D = {
    x: room.width / 3,
    y: room.depth - margin,
    z: 0,
  };

  // Place cooktop on the left wall (x = margin)
  const cooktopPos: Point3D = {
    x: margin,
    y: room.depth / 2,
    z: 0,
  };

  // Place fridge near the entrance (front-right)
  const fridgePos: Point3D = {
    x: room.width - margin - 60,
    y: margin,
    z: 0,
  };

  const placements: LayoutPlacement[] = [
    {
      productId: DEFAULT_PRODUCTS.sink,
      role: "sink",
      position: sinkPos,
      rotation: 0,
    },
    {
      productId: DEFAULT_PRODUCTS.cooktop,
      role: "cooktop",
      position: cooktopPos,
      rotation: 90,
    },
    {
      productId: DEFAULT_PRODUCTS.fridge,
      role: "fridge",
      position: fridgePos,
      rotation: 0,
    },
  ];

  // Calculate work triangle metrics
  const triangle = calculateTriangleMetrics(placements);
  const score = calculateTriangleScore(triangle);

  return {
    id: `layout-default-${Date.now()}`,
    type: "L-shape",
    score,
    placements,
    triangle,
    reasoning:
      "Default L-shape layout based on room dimensions. Define walls for more accurate suggestions.",
    source: "fallback",
  };
}

/**
 * Generate a single optimized layout
 */
export function generateOptimalLayout(
  room: RoomConfig,
  walls: WallSegment[],
  elements: StructuralElement[]
): LayoutSuggestion {
  const response = generateFallbackLayouts(room, walls, elements);
  return response.layouts[0];
}
